/**
 * PHASE 7 MODULE 7.1: Task List Section
 *
 * Displays work items (tasks) for a project with:
 * - Create task form
 * - Inline status change
 * - Assignee display
 * - Due date display
 * - Comment panel
 * - Activity log
 *
 * OPTIMISTIC UPDATES:
 * - Status change: instant UI update, rollback on error
 * - Create task: temp ID appears immediately, replaced on success
 * - Add comment: temp comment appears, replaced/removed on success/error
 * - Bulk delete: optimistic remove, rollback on error
 * - Bulk status: NOT optimistic (STRICT validation may reject)
 */
import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from 'react';
import type { ReactNode } from 'react';
import { formatCalendarDate } from '@/utils/calendarDate';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useProjectContext } from '@/features/projects/layout/ProjectPageLayout';
import { useRouteAction } from '@/features/projects/useRouteAction';
import { useProjectColumnVisibility } from '@/features/projects/columns/useProjectColumnVisibility';
import { taskActionErrorMessage } from '@/features/projects/tabs/taskActionErrorMessage';
import { Plus, ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { useWorkspaceStore } from '@/state/workspace.store';
import { useAuth } from '@/state/AuthContext';
import { isPlatformAdmin, isPlatformViewer } from '@/utils/access';
import { useWorkspaceRole } from '@/hooks/useWorkspaceRole';
import { Button } from '@/components/ui/Button';
import { listWorkspaceMembers } from '@/features/workspaces/workspace.api';
import {
  addComment,
  addDependency,
  createTask,
  deleteTask,
  listActivity,
  listComments,
  listDependencies,
  bulkUpdate,
  listTasks,
  removeDependency,
  restoreTask,
  updateTask,
  getAllowedTransitions,
  type TaskActivityItem,
  type TaskComment,
  type TaskDependency,
  type UpdateTaskPatch,
  type WorkTask,
  type WorkTaskPriority,
  type WorkTaskStatus,
} from '@/features/work-management/workTasks.api';
import {
  WORK_TASK_TYPES,
  type WorkTaskType,
} from '@/features/work-management/workTaskType.constants';
import { SharedCell } from '@/features/projects/columns/ColumnCells';
import { formatTaskActivitySentence } from '@/features/work-management/taskActivityFormat';
import {
  notifyGovernanceBulkPartialSuccess,
  notifyGovernanceRuleBlocked,
} from '@/features/work-management/governanceTaskUpdateErrors';
import { notifyBulkSkipped } from '@/features/work-management/bulkUpdateSummary';
import { GovernanceBlockBanner } from '@/features/work-management/components/GovernanceBlockBanner';
import { TaskClosureReasonDialog } from '@/features/work-management/components/TaskClosureReasonDialog';
import {
  cancelClosureBody,
  closureRequiredForPhase,
  deferralClosureBody,
  deferralDecision,
  isExecutionLockResetAckRequired,
  moveMenuPhases,
  phaseFromPlan,
  useGatesFromPlan,
  type ClosurePhase,
} from '@/features/work-management/taskClosure';
import type { TaskClosureKind } from '@/features/work-management/workTasks.api';
import { invalidateStatsCache } from '@/features/work-management/workTasks.stats.api';
import { AcceptanceCriteriaEditor } from '@/features/work-management/components/AcceptanceCriteriaEditor';
import { CompletionBar } from '@/features/work-management/components/CompletionBar';
import { computeTaskCompletion } from '@/features/work-management/statusWeights';
import { computeDurationDays } from '@/features/work-management/statusBucket';
import type { ProjectColumnKey } from '@/features/projects/columns';
import {
  COLUMN_REGISTRY,
  getDefaultColumnsForMethodology,
  getPickableColumns,
  SprintCell,
  useProjectSprints,
} from '@/features/projects/columns';
import {
  filterColumnsForCapabilities,
  useProjectCapabilities,
} from '@/features/projects/capabilities';
import { useSprintTaskAssignmentMutations } from '@/features/projects/hooks/useSprintTaskAssignmentMutations';
import {
  workTasksByProjectQueryKey,
  type WorkTasksByProjectData,
} from '@/features/projects/workTasksQueryKey';
import { filtersFromParams, taskMatchesFilters } from '@/features/projects/components/FilterBar';
import { WORK_SURFACE_QUERY } from '@/features/projects/workSurface/workSurfaceQuery';
import {
  parseSortDir,
  parseWorkSurfaceSortKey,
  sortWorkTasks,
} from '@/features/projects/workSurface/workSurfaceTaskSort';
import { request } from '@/lib/api';
import {
  ActivitiesTaskRowMenu,
  type ActivitiesPlanPhase,
  type TaskConvertType,
  type TaskLinkRelation,
} from './ActivitiesTaskRowMenu';
import { linkTaskDependency } from './linkTaskDependency';
import { TaskDetailPanel } from '../waterfall/TaskDetailPanel';
import { UnifiedWorkFieldsPanel } from '@/features/projects/fields/UnifiedWorkFieldsPanel';
import { AttributeCell } from '@/features/attributes/components/AttributeCell';
import {
  batchGetAttributeValues,
  upsertTaskAttributeValue,
} from '@/features/attributes/attributes.api';
import { useProjectAttributeColumns } from '@/features/attributes/useProjectAttributeColumns';
import {
  isAttributeTypeMismatch,
  mapAttributeApiError,
} from '@/features/attributes/mapAttributeApiError';
import { compareAttributeValues } from '@/features/attributes/attributeValue.utils';

const ACTIVITIES_STATUS_GROUPS = [
  {
    groupLabel: 'Not started',
    options: [
      { value: 'BACKLOG' as const, label: 'Backlog', swatch: 'bg-slate-200', text: 'text-slate-700' },
      { value: 'TODO' as const, label: 'To do', swatch: 'bg-slate-300', text: 'text-slate-800' },
    ],
  },
  {
    groupLabel: 'Active',
    options: [
      { value: 'IN_PROGRESS' as const, label: 'In progress', swatch: 'bg-blue-500', text: 'text-white' },
      { value: 'IN_REVIEW' as const, label: 'In review', swatch: 'bg-violet-500', text: 'text-white' },
      { value: 'BLOCKED' as const, label: 'Blocked', swatch: 'bg-amber-500', text: 'text-white' },
    ],
  },
  {
    groupLabel: 'Closed',
    options: [
      { value: 'DONE' as const, label: 'Done', swatch: 'bg-emerald-500', text: 'text-white' },
      { value: 'CANCELED' as const, label: 'Canceled', swatch: 'bg-slate-400', text: 'text-white' },
    ],
  },
];

// Generate temporary ID for optimistic inserts
function tempId(): string {
  return `temp:${crypto.randomUUID()}`;
}

// Error code constants
const ERR_WORKSPACE_REQUIRED = 'WORKSPACE_REQUIRED';
const ERR_VALIDATION_ERROR = 'VALIDATION_ERROR';

/** Matches backend list cap and Waterfall/Board loads (see work-tasks MAX_LIMIT). */
const WORK_TASK_LIST_PAGE_SIZE = 200;

// Extract error details from API response
function getErrorDetails(error: any): { code?: string; message?: string; invalidTransitions?: any[] } {
  const data = error?.response?.data || error;
  return {
    code: data?.code,
    message: data?.message || 'An error occurred',
    invalidTransitions: data?.invalidTransitions,
  };
}

type WorkspaceMember = {
  id: string;
  userId: string;
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
};

interface Props {
  projectId: string;
  workspaceId: string;
  /** Drives default Activities table columns (agile / scrum / kanban / hybrid / waterfall). */
  methodology?: string | null;
}

export function TaskListSection({
  projectId,
  workspaceId,
  methodology = null,
}: Props) {
  const { isReadOnly } = useProjectContext();
  const projectClosed = isReadOnly;
  const [searchParams] = useSearchParams();
  const taskQ = searchParams.get(WORK_SURFACE_QUERY.taskQ) ?? '';
  const myTasksOnly = searchParams.get(WORK_SURFACE_QUERY.myTasks) === '1';
  const urlFilters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const sortKey = useMemo(
    () => parseWorkSurfaceSortKey(searchParams.get(WORK_SURFACE_QUERY.sort)),
    [searchParams],
  );
  const sortDir = useMemo(
    () => parseSortDir(searchParams.get(WORK_SURFACE_QUERY.sortDir)),
    [searchParams],
  );

  const hasWorkspaceMismatch = !workspaceId;
  const { user } = useAuth();
  const { isReadOnly: workspaceReadOnly } = useWorkspaceRole(workspaceId);
  const { getWorkspaceMembers, setWorkspaceMembers } = useWorkspaceStore();
  const queryClient = useQueryClient();

  const workListKey = useMemo(
    () =>
      projectId && workspaceId && !hasWorkspaceMismatch
        ? workTasksByProjectQueryKey(workspaceId, projectId)
        : null,
    [projectId, workspaceId, hasWorkspaceMismatch],
  );

  const tasksQuery = useQuery({
    queryKey: workListKey ?? (['work-tasks', '__off'] as const),
    queryFn: async (): Promise<WorkTasksByProjectData> => {
      const result = await listTasks({
        projectId: projectId!,
        limit: WORK_TASK_LIST_PAGE_SIZE,
      });
      const items = Array.isArray(result.items) ? result.items : [];
      return { items, total: result.total };
    },
    enabled: workListKey !== null,
  });

  const setTasks = useCallback(
    (updater: React.SetStateAction<WorkTask[]>) => {
      if (!workListKey) return;
      queryClient.setQueryData<WorkTasksByProjectData>(workListKey, (old) => {
        const prevItems = old?.items ?? [];
        const nextItems =
          typeof updater === 'function'
            ? (updater as (prev: WorkTask[]) => WorkTask[])(prevItems)
            : updater;
        return {
          items: nextItems,
          total: old?.total ?? nextItems.length,
        };
      });
    },
    [queryClient, workListKey],
  );

  const tasks = workListKey ? (tasksQuery.data?.items ?? []) : [];
  const taskListMayBeIncomplete =
    workListKey && tasksQuery.data
      ? tasksQuery.data.total > tasksQuery.data.items.length
      : false;
  const loading = Boolean(workListKey) && tasksQuery.isPending;

  const visibleTasks = useMemo(() => {
    let list = tasks.filter((t) => taskMatchesFilters(t, urlFilters));
    if (myTasksOnly) {
      if (!user?.id) return [];
      list = list.filter((t) => t.assigneeUserId === user.id);
    }
    const q = taskQ.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          (t.description ?? '').toLowerCase().includes(q) ||
          (t.remarks ?? '').toLowerCase().includes(q),
      );
    }
    return sortWorkTasks(list, sortKey, sortDir);
  }, [tasks, urlFilters, myTasksOnly, user?.id, taskQ, sortKey, sortDir]);

  const capabilities = useProjectCapabilities();

  const propertyColumnOrder = useMemo(
    () =>
      filterColumnsForCapabilities(
        getDefaultColumnsForMethodology(
          methodology && methodology.trim() ? methodology : 'agile',
        ),
        capabilities,
      ),
    [methodology, capabilities],
  );

  // COLUMN-RENDER-1: every pickable column is available in the Fields panel;
  // the methodology's own columns start shown, the rest start hidden.
  const pickableColumns = useMemo(
    () =>
      filterColumnsForCapabilities(
        getPickableColumns(methodology && methodology.trim() ? methodology : 'agile'),
        capabilities,
      ),
    [methodology, capabilities],
  );
  const optionalDefaultHidden = useMemo(
    () => pickableColumns.filter((k) => !propertyColumnOrder.includes(k)),
    [pickableColumns, propertyColumnOrder],
  );
  const { hidden: hiddenPropertyColumns, toggle: togglePropertyColumn } =
    useProjectColumnVisibility(optionalDefaultHidden);

  const activitiesTableColumns = useMemo(
    () => pickableColumns.filter((k) => !hiddenPropertyColumns.has(k)),
    [pickableColumns, hiddenPropertyColumns],
  );

  const [showAttributePanel, setShowAttributePanel] = useState(false);
  const attributePanelAnchorRef = useRef<HTMLButtonElement>(null);
  const {
    available: availableAttributes,
    visibleIds: visibleAttributeIds,
    visibleDefinitions: visibleAttributeDefinitions,
    loadFailed: attributesLoadFailed,
    toggleColumn: toggleAttributeColumn,
    onCreated: onAttributeCreated,
    retry: retryAttributes,
  } = useProjectAttributeColumns(workspaceId, projectId);
  const [attributeValues, setAttributeValues] = useState<Record<string, Record<string, unknown>>>({});
  const [editingAttributeCell, setEditingAttributeCell] = useState<{
    taskId: string;
    definitionId: string;
  } | null>(null);
  const [attributeCommitError, setAttributeCommitError] = useState<{
    taskId: string;
    definitionId: string;
    message: string;
  } | null>(null);
  const [attributeSort, setAttributeSort] = useState<{ defId: string; dir: 'asc' | 'desc' } | null>(
    null,
  );

  const sortedVisibleTasks = useMemo(() => {
    if (!attributeSort) return visibleTasks;
    const def = visibleAttributeDefinitions.find((d) => d.id === attributeSort.defId);
    if (!def) return visibleTasks;
    const dir = attributeSort.dir === 'asc' ? 1 : -1;
    return [...visibleTasks].sort(
      (a, b) =>
        compareAttributeValues(
          def.dataType,
          attributeValues[a.id]?.[attributeSort.defId],
          attributeValues[b.id]?.[attributeSort.defId],
        ) * dir,
    );
  }, [visibleTasks, attributeSort, attributeValues, visibleAttributeDefinitions]);

  const handleAttributeHeaderSort = (defId: string) => {
    setAttributeSort((prev) => {
      if (prev?.defId === defId) {
        return { defId, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { defId, dir: 'asc' };
    });
  };

  const attributeTrailingColCount = visibleAttributeDefinitions.length + 1;

  useEffect(() => {
    if (!workspaceId || visibleAttributeDefinitions.length === 0 || visibleTasks.length === 0) {
      return;
    }
    const taskIds = visibleTasks.map((t) => t.id);
    let cancelled = false;
    batchGetAttributeValues(taskIds, workspaceId)
      .then((batch) => {
        if (cancelled) return;
        setAttributeValues((prev) => {
          const next = { ...prev };
          for (const taskId of taskIds) {
            const fromBatch = batch[taskId];
            if (!fromBatch) continue;
            next[taskId] = { ...(next[taskId] ?? {}), ...fromBatch };
          }
          return next;
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [workspaceId, visibleTasks, visibleAttributeDefinitions]);

  const { sprintMap, activeSprints, planningSprints } = useProjectSprints(
    projectId,
    capabilities.use_iterations,
  );
  const sprintAssignment = useSprintTaskAssignmentMutations(workspaceId, projectId);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [comments, setComments] = useState<Record<string, TaskComment[]>>({});
  const [activities, setActivities] = useState<Record<string, TaskActivityItem[]>>({});
  const [showComments, setShowComments] = useState<Record<string, boolean>>({});
  const [showActivity, setShowActivity] = useState<Record<string, boolean>>({});
  const [showDeps, setShowDeps] = useState<Record<string, boolean>>({});
  const [showAC, setShowAC] = useState<Record<string, boolean>>({});
  const [deps, setDeps] = useState<Record<string, { predecessors: TaskDependency[]; successors: TaskDependency[] }>>({});
  const [addingDep, setAddingDep] = useState<Record<string, boolean>>({});
  const [depSearch, setDepSearch] = useState<Record<string, string>>({});
  const [newComment, setNewComment] = useState<Record<string, string>>({});
  const [postingComment, setPostingComment] = useState<Record<string, boolean>>({});

  // PHASE 7 MODULE 7.4: Bulk actions state
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'status' | 'assign' | 'dueDate' | 'clearDueDate' | 'unassign' | 'delete' | null>(null);
  const [bulkStatus, setBulkStatus] = useState<WorkTaskStatus>('TODO');
  const [bulkAssigneeId, setBulkAssigneeId] = useState<string>('');
  const [bulkDueDate, setBulkDueDate] = useState<string>('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Toolbar filters — do not keep bulk selection for rows that are not visible.
  useEffect(() => {
    const allowed = new Set(visibleTasks.map((t) => t.id));
    setSelectedTaskIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (allowed.has(id)) next.add(id);
        else changed = true;
      }
      if (!changed && next.size === prev.size) return prev;
      return next;
    });
  }, [visibleTasks]);

  // OPTIMISTIC UPDATES: Rollback storage for failed operations
  const rollbackTasks = useRef<Map<string, WorkTask>>(new Map());
  const rollbackComments = useRef<Map<string, TaskComment[]>>(new Map());
  /** Status changes show intent (pending), never an unconfirmed outcome. */
  const [pendingStatusIds, setPendingStatusIds] = useState<Set<string>>(new Set());
  const [statusActionErrors, setStatusActionErrors] = useState<Record<string, string>>({});

  // ADMIN ONLY: Recently deleted tasks panel
  const [showDeletedPanel, setShowDeletedPanel] = useState(false);
  const [deletedTasks, setDeletedTasks] = useState<WorkTask[]>([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [restoringTaskIds, setRestoringTaskIds] = useState<Set<string>>(new Set());
  const deletedInFlight = useRef(false);

  // PHASE 7 MODULE 7.1 FIX: Use cached members
  const cachedMembers = workspaceId ? getWorkspaceMembers(workspaceId) : null;
  const [workspaceMembers, setWorkspaceMembersState] = useState<WorkspaceMember[]>(cachedMembers || []);

  // Phase 3 (Template Center): per-project team — Activities assignee pool is filtered to this set
  const [projectTeamMemberIds, setProjectTeamMemberIds] = useState<string[] | null>(null);

  // Phase 3: project-level linked documents (surfaced in Activities, managed in Overview)
  const [projectDocs, setProjectDocs] = useState<Array<{ id: string; title: string }>>([]);

  const [planPhases, setPlanPhases] = useState<ActivitiesPlanPhase[]>([]);
  const [closurePhases, setClosurePhases] = useState<ClosurePhase[]>([]);
  const [planUseGates, setPlanUseGates] = useState<boolean | null>(null);
  const [closurePrompt, setClosurePrompt] = useState<{
    taskIds: string[];
    kind: TaskClosureKind;
    phaseId?: string;
    bulk?: boolean;
  } | null>(null);
  const useGates = planUseGates ?? capabilities.use_gates;
  const [detailPanelTaskId, setDetailPanelTaskId] = useState<string | null>(null);
  const [pendingDeleteTask, setPendingDeleteTask] = useState<WorkTask | null>(null);
  const [pendingBulkDeleteCount, setPendingBulkDeleteCount] = useState<number | null>(null);
  const [titleRenameTaskId, setTitleRenameTaskId] = useState<string | null>(null);

  // Phase 3: assignee pool is project team only (fall back to all workspace members if team not yet loaded or empty)
  const assigneePool = useMemo(() => {
    if (!projectTeamMemberIds || projectTeamMemberIds.length === 0) {
      // No project team set yet — fall back to workspace members so picker isn't empty
      return workspaceMembers;
    }
    const teamSet = new Set(projectTeamMemberIds);
    return workspaceMembers.filter((m: any) => {
      const id = m.userId || m.user?.id;
      return id && teamSet.has(id);
    });
  }, [workspaceMembers, projectTeamMemberIds]);

  const taskChildrenByParent = useMemo(() => {
    const m = new Map<string, WorkTask[]>();
    for (const t of tasks) {
      if (t.deletedAt) continue;
      if (!t.parentTaskId) continue;
      const arr = m.get(t.parentTaskId) ?? [];
      arr.push(t);
      m.set(t.parentTaskId, arr);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
    }
    return m;
  }, [tasks]);

  // PHASE 7 MODULE 7.1 FIX: Consistent role checks (canonical helpers)
  const isAdmin = isPlatformAdmin(user);
  const isGuest = isPlatformViewer(user);
  const canEdit = !workspaceReadOnly && !isReadOnly && !isGuest;
  // Project "+" menu deep link (PROJECT-MENU-1): opens the New Task form for a caller who can edit.
  useRouteAction('new-task', canEdit, () => setShowCreateForm(true));

  // Handle WORKSPACE_REQUIRED errors consistently
  const handleWorkspaceError = useCallback(() => {
    toast.error('Workspace selection required. Please select a workspace.');
    // Could redirect to workspace picker here if needed
  }, []);

  useEffect(() => {
    if (projectId && workspaceId) {
      loadWorkspaceMembers();
      loadProjectTeam();
    }
  }, [projectId, workspaceId]);

  // Phase 3: load per-project team to filter Activities assignee pool
  async function loadProjectTeam() {
    if (!projectId) return;
    try {
      const { projectsApi } = await import('@/features/projects/projects.api');
      const res = await projectsApi.getProjectTeam(projectId);
      setProjectTeamMemberIds(res.teamMemberIds || []);
    } catch {
      setProjectTeamMemberIds([]);
    }
  }

  // Phase 3: load project-level linked documents (surfaced in Activities, managed in Overview)
  useEffect(() => {
    if (!projectId || !workspaceId) return;
    let cancelled = false;
    (async () => {
      try {
        const { api } = await import('@/lib/api');
        const res: any = await api.get(`/work/workspaces/${workspaceId}/projects/${projectId}/documents`);
        const data = res?.data ?? res;
        const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        if (!cancelled) {
          setProjectDocs(items.map((d: any) => ({ id: d.id, title: d.title })));
        }
      } catch {
        if (!cancelled) setProjectDocs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, workspaceId]);

  useEffect(() => {
    if (!projectId || !workspaceId) return;
    let cancelled = false;
    (async () => {
      try {
        const planPayload = await request.get<{
          capabilities?: { use_gates?: boolean };
          phases?: Array<{
            id: string;
            name: string;
            sortOrder?: number;
            gate?: { definitionExists?: boolean } | null;
          }>;
        }>(`/work/projects/${projectId}/plan`, {
          headers: { 'x-workspace-id': workspaceId },
        });
        if (!cancelled) {
          const mapped = (planPayload?.phases ?? []).map((phase) => phaseFromPlan(phase));
          setClosurePhases(mapped);
          setPlanUseGates(useGatesFromPlan(planPayload));
          setPlanPhases(mapped.map((phase) => ({ id: phase.id, name: phase.name })));
        }
      } catch {
        if (!cancelled) setPlanPhases([]);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, workspaceId]);

  async function loadWorkspaceMembers() {
    if (!workspaceId) return;

    // PHASE 7 MODULE 7.1 FIX: Check cache first
    const cached = getWorkspaceMembers(workspaceId);
    if (cached) {
      setWorkspaceMembersState(cached);
      return;
    }

    try {
      const members = await listWorkspaceMembers(workspaceId);
      const normalized = (members || [])
        .filter((member): member is WorkspaceMember => typeof member.userId === 'string' && member.userId.length > 0)
        .map((member) => {
          const base = { id: member.id, userId: member.userId };
          if (member.user) {
            return { ...base, user: { ...member.user, id: member.user.id || member.userId } };
          }
          return base;
        });
      setWorkspaceMembers(workspaceId, normalized); // Cache it
      setWorkspaceMembersState(normalized);
    } catch (error) {
      console.error('Failed to load workspace members:', error);
    }
  }

  async function loadComments(taskId: string) {
    try {
      const result = await listComments(taskId);
      setComments(prev => ({ ...prev, [taskId]: result.items || [] }));
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  }

  async function loadActivities(taskId: string) {
    try {
      const result = await listActivity(taskId);
      setActivities(prev => ({ ...prev, [taskId]: result.items || [] }));
    } catch (error) {
      console.error('Failed to load activities:', error);
    }
  }

  async function handleCreateTask(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!projectId || !workspaceId) return;
    if (hasWorkspaceMismatch) {
      handleWorkspaceError();
      return;
    }

    const formData = new FormData(e.currentTarget);
    const title = formData.get('title') as string;
    const description = formData.get('description') as string;
    const assigneeId = (formData.get('assigneeId') as string) || undefined;
    const dueDate = formData.get('dueDate') as string || undefined;
    const startDate = formData.get('startDate') as string || undefined;
    const taskType = (formData.get('type') as WorkTaskType) || 'TASK';

    if (!title.trim()) return;
    // ISO dates compare as text; a start after the due date would draw an inverted Gantt bar.
    if (startDate && dueDate && startDate > dueDate) {
      toast.error('The start date is after the due date.');
      return;
    }

    // OPTIMISTIC: Create temp task and insert immediately
    // FIX #3: Use placeholder values for server-only fields, mark as optimistic to avoid leaking to analytics
    const tempTaskId = tempId();
    const nowIso = new Date().toISOString();
    const tempTask: WorkTask = {
      id: tempTaskId,
      organizationId: 'temp-org', // Placeholder - replaced by server on success
      workspaceId: workspaceId,
      projectId,
      parentTaskId: null,
      phaseId: null,
      title: title.trim(),
      description: description?.trim() || null,
      status: 'TODO' as WorkTaskStatus,
      type: taskType,
      priority: 'MEDIUM',
      assigneeUserId: assigneeId || null,
      reporterUserId: user?.id ?? 'temp-user', // Placeholder if no user
      startDate: startDate || null,
      dueDate: dueDate || null,
      completedAt: null,
      estimatePoints: null,
      estimateHours: null,
      remainingHours: null,
      actualHours: null,
      actualStartAt: null,
      actualEndAt: null,
      iterationId: null,
      committed: false,
      rank: null,
      tags: ['__optimistic__'], // Marker to identify temp tasks, stripped on server replace
      metadata: { optimistic: true }, // Flag to prevent leaking to analytics
      acceptanceCriteria: [],
      createdAt: nowIso,
      updatedAt: nowIso,
      deletedAt: null,
      deletedByUserId: null,
      approvalStatus: 'not_required',
      documentRequired: false,
      remarks: null,
      isMilestone: false,
    };

    // Insert temp task at top of list
    setTasks(prev => [tempTask, ...prev]);
    setShowCreateForm(false);
    e.currentTarget.reset();
    setCreating(true);

    try {
      const input = {
        projectId,
        title: title.trim(),
      } as Parameters<typeof createTask>[0];
      const trimmedDescription = description?.trim();
      if (trimmedDescription) input.description = trimmedDescription;
      if (assigneeId) input.assigneeUserId = assigneeId;
      if (dueDate) input.dueDate = dueDate;
      if (startDate) input.startDate = startDate;
      if (taskType && taskType !== 'TASK') input.type = taskType;

      const created = await createTask(input);

      // Replace temp task with real task from server
      setTasks(prev => prev.map(t => t.id === tempTaskId ? created : t));
      toast.success('Task created');

      // Invalidate stats cache and dispatch event for KPI invalidation
      invalidateStatsCache(projectId);
      window.dispatchEvent(new CustomEvent('task:changed', { detail: { projectId } }));
    } catch (error: any) {
      console.error('Failed to create task:', error);
      // Remove temp task on error
      setTasks(prev => prev.filter(t => t.id !== tempTaskId));
      const { code, message } = getErrorDetails(error);
      if (code === ERR_WORKSPACE_REQUIRED) {
        handleWorkspaceError();
      } else if (notifyGovernanceRuleBlocked(error, { projectId, workspaceId })) {
        // Governance toast already shown
      } else {
        toast.error(message || 'Failed to create task');
      }
      setShowCreateForm(true); // Re-open form so user can retry
    } finally {
      setCreating(false);
    }
  }

  async function handleStatusChange(taskId: string, newStatus: WorkTaskStatus) {
    if (!canEdit) return;
    if (hasWorkspaceMismatch) {
      handleWorkspaceError();
      return;
    }
    const currentTask = tasks.find((item) => item.id === taskId);
    if (
      currentTask &&
      newStatus === 'CANCELED' &&
      currentTask.status !== 'CANCELED' &&
      closureRequiredForPhase(
        useGates,
        closurePhases.find((phase) => phase.id === currentTask.phaseId),
      )
    ) {
      setClosurePrompt({ taskIds: [taskId], kind: 'CANCELLED' });
      return;
    }

    // FIX #5: Capture prev status and store rollback inside functional update
    // to avoid stale closure if user toggles twice fast
    let prevStatus: WorkTaskStatus | null = null;

    setTasks(prev => {
      const currentTask = prev.find(t => t.id === taskId);
      if (!currentTask) return prev;

      prevStatus = currentTask.status;
      rollbackTasks.current.set(taskId, { ...currentTask });

      return prev.map(t =>
        t.id === taskId
          ? { ...t, status: newStatus, updatedAt: new Date().toISOString() }
          : t
      );
    });

    // If task wasn't found, abort
    if (prevStatus === null) return;
    const capturedPrevStatus = prevStatus; // TypeScript narrowing

    setPendingStatusIds((prev) => new Set(prev).add(taskId));
    setStatusActionErrors((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });

    try {
      const updated = await updateTask(taskId, { status: newStatus });

      // Reconcile with server response
      setTasks(prev => prev.map(t => t.id === taskId ? updated : t));
      rollbackTasks.current.delete(taskId);

      // Invalidate stats cache and dispatch event for KPI invalidation
      invalidateStatsCache(projectId);
      window.dispatchEvent(new CustomEvent('task:changed', { detail: { projectId } }));

      // Background refresh activities if panel is open
      if (activities[taskId]) {
        loadActivities(taskId);
      }
    } catch (error: any) {
      console.error('Failed to update status:', error);
      // ROLLBACK: Restore previous state (no stats invalidation on error)
      const rollback = rollbackTasks.current.get(taskId);
      if (rollback) {
        setTasks(prev => prev.map(t => t.id === taskId ? rollback : t));
        rollbackTasks.current.delete(taskId);
      }

      const { code, message } = getErrorDetails(error);
      const blockReason =
        error?.response?.data?.policyMessages?.[0] ||
        error?.response?.data?.message ||
        message;
      setStatusActionErrors((prev) => ({
        ...prev,
        [taskId]:
          code === 'GOVERNANCE_RULE_BLOCKED'
            ? String(blockReason || 'Action blocked by governance')
            : code === 'INVALID_STATUS_TRANSITION'
              ? `Cannot change from ${capturedPrevStatus} to ${newStatus}`
              : String(message || 'Failed to update status'),
      }));

      if (code === ERR_WORKSPACE_REQUIRED) {
        handleWorkspaceError();
      } else if (code === 'INVALID_STATUS_TRANSITION') {
        // Inline row error already set
      } else if (notifyGovernanceRuleBlocked(error, { projectId, workspaceId })) {
        // Toast + flash for navigation; row shows reason without a second click
      }
    } finally {
      setPendingStatusIds((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  }

  /** Generic optimistic PATCH for fields other than status (status uses transition validation UX). */
  async function optimisticPatchTask(
    taskId: string,
    patch: UpdateTaskPatch,
    options?: { propagateResetAck?: boolean },
  ) {
    if (!canEdit) return;
    if (hasWorkspaceMismatch) {
      handleWorkspaceError();
      return;
    }

    let found = false;
    setTasks((prev) => {
      const current = prev.find((t) => t.id === taskId);
      if (!current) return prev;
      found = true;
      rollbackTasks.current.set(taskId, { ...current });
      return prev.map((t) =>
        t.id === taskId
          ? ({ ...t, ...patch, updatedAt: new Date().toISOString() } as WorkTask)
          : t,
      );
    });
    if (!found) return;

    try {
      const updated = await updateTask(taskId, patch);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
      rollbackTasks.current.delete(taskId);
      invalidateStatsCache(projectId);
      window.dispatchEvent(new CustomEvent('task:changed', { detail: { projectId } }));
      if (activities[taskId]) {
        loadActivities(taskId);
      }
    } catch (error: any) {
      const rollback = rollbackTasks.current.get(taskId);
      if (rollback) {
        setTasks((prev) => prev.map((t) => (t.id === taskId ? rollback : t)));
        rollbackTasks.current.delete(taskId);
      }
      if (options?.propagateResetAck && isExecutionLockResetAckRequired(error)) {
        throw error;
      }
      console.error('Failed to update task:', error);
      const { code, message } = getErrorDetails(error);
      if (code === ERR_WORKSPACE_REQUIRED) {
        handleWorkspaceError();
      } else if (notifyGovernanceRuleBlocked(error, { projectId, workspaceId })) {
        // toast already shown
      } else {
        toast.error(message || 'Failed to update task');
      }
    }
  }

  async function onCommitAttribute(taskId: string, definitionId: string, value: unknown) {
    if (!canEdit) return;
    const snapshot = attributeValues;
    setAttributeValues((prev) => {
      const taskVals = { ...(prev[taskId] ?? {}) };
      if (value === null || value === undefined || value === '') {
        delete taskVals[definitionId];
      } else {
        taskVals[definitionId] = value;
      }
      return { ...prev, [taskId]: taskVals };
    });
    setEditingAttributeCell(null);
    setAttributeCommitError(null);

    try {
      const def = availableAttributes.find((d) => d.id === definitionId);
      await upsertTaskAttributeValue(taskId, definitionId, value, workspaceId, def?.dataType);
    } catch (err) {
      setAttributeValues(snapshot);
      const mapped = mapAttributeApiError(err);
      setAttributeCommitError({ taskId, definitionId, message: mapped.message });
      if (!isAttributeTypeMismatch(err)) {
        toast.error(mapped.message);
      }
    }
  }

  const handleSprintReassign = useCallback(
    async (taskId: string, nextIterationId: string | null) => {
      if (!canEdit) return;
      if (hasWorkspaceMismatch) {
        handleWorkspaceError();
        return;
      }
      const task = tasks.find((t) => t.id === taskId);
      if (!task) return;
      const previousIterationId = task.iterationId ?? null;
      if (previousIterationId === nextIterationId) return;
      try {
        await sprintAssignment.mutateAsync({
          taskId,
          previousIterationId,
          nextIterationId,
        });
      } catch (error: any) {
        const { code, message } = getErrorDetails(error);
        if (code === ERR_WORKSPACE_REQUIRED) {
          handleWorkspaceError();
        } else {
          toast.error(message || 'Failed to update sprint assignment');
        }
      }
    },
    [canEdit, hasWorkspaceMismatch, tasks, sprintAssignment, handleWorkspaceError],
  );

  async function handleAddComment(taskId: string) {
    const commentText = newComment[taskId];
    if (!commentText?.trim()) return;
    if (hasWorkspaceMismatch) {
      handleWorkspaceError();
      return;
    }

    // OPTIMISTIC: Create temp comment and insert immediately
    const tempCommentId = tempId();
    const nowIso = new Date().toISOString();
    const tempComment: TaskComment = {
      id: tempCommentId,
      taskId,
      body: commentText.trim(),
      authorUserId: user?.id || 'unknown',
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    // FIX #4: Store rollback inside functional update to avoid stale closure
    setComments(prev => {
      rollbackComments.current.set(taskId, prev[taskId] || []);
      return {
        ...prev,
        [taskId]: [...(prev[taskId] || []), tempComment],
      };
    });
    setNewComment(prev => ({ ...prev, [taskId]: '' }));
    setPostingComment(prev => ({ ...prev, [taskId]: true }));

    try {
      const created = await addComment(taskId, commentText.trim());

      // Replace temp comment with real comment from server
      setComments(prev => ({
        ...prev,
        [taskId]: (prev[taskId] || []).map(c =>
          c.id === tempCommentId ? created : c
        ),
      }));
      rollbackComments.current.delete(taskId);

      // Background refresh activities if panel is open
      if (activities[taskId]) {
        loadActivities(taskId);
      }
    } catch (error: any) {
      console.error('Failed to add comment:', error);
      // ROLLBACK: Restore previous comments
      const rollback = rollbackComments.current.get(taskId);
      if (rollback !== undefined) {
        setComments(prev => ({ ...prev, [taskId]: rollback }));
        rollbackComments.current.delete(taskId);
      } else {
        // Fallback: remove temp comment
        setComments(prev => ({
          ...prev,
          [taskId]: (prev[taskId] || []).filter(c => c.id !== tempCommentId),
        }));
      }

      const { code, message } = getErrorDetails(error);
      if (code === ERR_WORKSPACE_REQUIRED) {
        handleWorkspaceError();
      } else {
        toast.error(message || 'Failed to add comment');
      }
    } finally {
      setPostingComment(prev => ({ ...prev, [taskId]: false }));
    }
  }

  function toggleComments(taskId: string) {
    const isOpen = showComments[taskId];
    setShowComments(prev => ({ ...prev, [taskId]: !isOpen }));
    if (!isOpen && !comments[taskId]) {
      loadComments(taskId);
    }
  }

  function toggleActivity(taskId: string) {
    const isOpen = showActivity[taskId];
    setShowActivity(prev => ({ ...prev, [taskId]: !isOpen }));
    if (!isOpen && !activities[taskId]) {
      loadActivities(taskId);
    }
  }

  function toggleDeps(taskId: string) {
    const isOpen = showDeps[taskId];
    setShowDeps(prev => ({ ...prev, [taskId]: !isOpen }));
    if (!isOpen && !deps[taskId]) {
      loadDeps(taskId);
    }
  }

  function toggleAC(taskId: string) {
    setShowAC(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  }

  async function handleSaveAC(taskId: string, items: Array<{ text: string; done: boolean }>) {
    const saved = await updateTask(taskId, { acceptanceCriteria: items });
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, acceptanceCriteria: saved.acceptanceCriteria } : t));
  }

  async function loadDeps(taskId: string) {
    try {
      const result = await listDependencies(taskId);
      setDeps(prev => ({ ...prev, [taskId]: result }));
    } catch (error) {
      console.error('Failed to load dependencies:', error);
    }
  }

  async function handleAddDep(taskId: string, predecessorTaskId: string) {
    if (addingDep[taskId]) return;
    setAddingDep(prev => ({ ...prev, [taskId]: true }));
    try {
      await addDependency(taskId, predecessorTaskId, 'FINISH_TO_START');
      toast.success('Dependency added');
      await loadDeps(taskId);
      setDepSearch(prev => ({ ...prev, [taskId]: '' }));
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to add dependency';
      toast.error(msg);
    } finally {
      setAddingDep(prev => ({ ...prev, [taskId]: false }));
    }
  }

  async function handleLinkTask(taskId: string, targetId: string, relation: TaskLinkRelation) {
    const linked = await linkTaskDependency(taskId, targetId, relation);
    if (!linked) return;
    // Refresh the dependency lists already on screen for both ends of the new edge.
    for (const id of [taskId, targetId]) {
      if (deps[id] || showDeps[id]) await loadDeps(id);
    }
  }

  async function handleRemoveDep(taskId: string, predecessorTaskId: string) {
    try {
      await removeDependency(taskId, predecessorTaskId);
      toast.success('Dependency removed');
      await loadDeps(taskId);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to remove dependency');
    }
  }

  const openDetailPanel = useCallback((taskId: string) => {
    setDetailPanelTaskId(taskId);
  }, []);

  const closeDetailPanel = useCallback(() => {
    setDetailPanelTaskId(null);
  }, []);

  const patchTaskForDetailPanel = useCallback(
    async (
      taskId: string,
      patch: Partial<{
        title: string;
        status: WorkTaskStatus;
        assigneeUserId: string | null;
        startDate: string | null;
        dueDate: string | null;
        remarks: string | null;
        phaseId: string;
        closureKind: 'CANCELLED' | 'NOT_APPLICABLE';
        closureReason: string;
        acknowledgeStatusReset: boolean;
      }>,
    ) => {
      const apiPatch: UpdateTaskPatch = {};
      if (patch.title !== undefined) apiPatch.title = patch.title;
      if (patch.status !== undefined) apiPatch.status = patch.status;
      if (patch.assigneeUserId !== undefined) apiPatch.assigneeUserId = patch.assigneeUserId;
      if (patch.startDate !== undefined) apiPatch.startDate = patch.startDate;
      if (patch.dueDate !== undefined) apiPatch.dueDate = patch.dueDate;
      if (patch.remarks !== undefined) apiPatch.remarks = patch.remarks;
      if (patch.phaseId !== undefined) apiPatch.phaseId = patch.phaseId;
      if (patch.closureKind !== undefined) apiPatch.closureKind = patch.closureKind;
      if (patch.closureReason !== undefined) apiPatch.closureReason = patch.closureReason;
      if (patch.acknowledgeStatusReset === true) apiPatch.acknowledgeStatusReset = true;
      await optimisticPatchTask(taskId, apiPatch, { propagateResetAck: true });
    },
    [optimisticPatchTask],
  );

  const handleDuplicateTask = useCallback(
    async (source: WorkTask) => {
      try {
        const created = await createTask({
          projectId,
          title: `${source.title} (copy)`,
          phaseId: source.phaseId ?? undefined,
          assigneeUserId: source.assigneeUserId ?? undefined,
          dueDate: source.dueDate ?? undefined,
          priority: source.priority,
          description: source.description ?? undefined,
          tags: source.tags ?? undefined,
        });
        setTasks((prev) => [...prev, created]);
        toast.success('Task duplicated');
      } catch (err: unknown) {
        if (notifyGovernanceRuleBlocked(err, { projectId, workspaceId })) return;
        toast.error(taskActionErrorMessage(err, 'Failed to duplicate task'));
      }
    },
    [projectId, setTasks],
  );

  const handleArchiveTask = useCallback(
    async (task: WorkTask) => {
      try {
        await updateTask(task.id, { archived: true });
        setTasks((prev) => prev.filter((t) => t.id !== task.id));
        setSelectedTaskIds((prev) => {
          if (!prev.has(task.id)) return prev;
          const next = new Set(prev);
          next.delete(task.id);
          return next;
        });
        toast.success('Task archived');
      } catch (err: unknown) {
        toast.error(taskActionErrorMessage(err, 'Failed to archive task'));
      }
    },
    [setTasks],
  );

  const executeDeleteTask = useCallback(
    async (task: WorkTask) => {
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      setSelectedTaskIds((prev) => {
        if (!prev.has(task.id)) return prev;
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
      try {
        await deleteTask(task.id);
        toast.success('Task deleted');
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Could not delete task';
        toast.error(message);
        if (workListKey) {
          await queryClient.invalidateQueries({ queryKey: workListKey });
        }
      }
    },
    [setTasks, workListKey, queryClient],
  );

  const handleMoveTaskToPhase = useCallback(
    async (taskId: string, phaseId: string) => {
      const current = tasks.find((item) => item.id === taskId);
      if (current) {
        const decision = deferralDecision(closurePhases, current, useGates);
        if (decision.required) {
          if (!decision.next || decision.next.id !== phaseId) {
            toast.error(
              decision.next
                ? `An unfinished task can only be deferred to ${decision.next.name}`
                : 'There is no next phase to defer this task into',
            );
            return;
          }
          setClosurePrompt({ taskIds: [taskId], kind: 'DEFERRED', phaseId });
          return;
        }
      }
      try {
        await optimisticPatchTask(taskId, { phaseId });
        toast.success('Task moved');
      } catch (err: unknown) {
        if (notifyGovernanceRuleBlocked(err, { projectId, workspaceId })) return;
        toast.error('Could not move task');
      }
    },
    [optimisticPatchTask, tasks, closurePhases, useGates],
  );

  const parentCandidatesFor = useCallback(
    (task: WorkTask) => {
      const blocked = new Set<string>();
      const stack = [task.id];
      while (stack.length) {
        const id = stack.pop()!;
        for (const t of tasks) {
          if (t.parentTaskId === id && !blocked.has(t.id)) {
            blocked.add(t.id);
            stack.push(t.id);
          }
        }
      }
      blocked.add(task.id);
      return tasks
        .filter((t) => !t.deletedAt && !blocked.has(t.id))
        .map((t) => ({ id: t.id, title: t.title }));
    },
    [tasks],
  );

  const handleCopyTaskLink = useCallback(
    async (taskId: string) => {
      const url = `${window.location.origin}/projects/${projectId}?taskId=${taskId}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied');
      } catch {
        toast.error('Could not copy link');
      }
    },
    [projectId],
  );

  const handlePromoteTask = useCallback(
    async (task: WorkTask) => {
      try {
        await optimisticPatchTask(task.id, { parentTaskId: null });
        toast.success('Task promoted to standalone task');
      } catch {
        toast.error('Failed to promote task');
      }
    },
    [optimisticPatchTask],
  );

  const handleConvertTask = useCallback(
    async (task: WorkTask, type: TaskConvertType) => {
      if (type === 'meeting_note') {
        toast.message('Meeting notes coming in next update');
        return;
      }
      if (type === 'milestone') {
        try {
          await optimisticPatchTask(task.id, { isMilestone: true });
          toast.success('Converted to milestone');
        } catch {
          toast.error('Failed to convert task');
        }
      }
    },
    [optimisticPatchTask],
  );

  const handleConvertToSubtask = useCallback(
    async (task: WorkTask, parentTaskId: string) => {
      try {
        await optimisticPatchTask(task.id, { parentTaskId });
        toast.success('Converted to subtask');
      } catch {
        toast.error('Failed to convert task');
      }
    },
    [optimisticPatchTask],
  );

  const handleAddSubtask = useCallback(
    async (parent: WorkTask) => {
      try {
        const created = await createTask({
          projectId,
          title: 'New subtask',
          parentTaskId: parent.id,
          phaseId: parent.phaseId ?? undefined,
        });
        setTasks((prev) => [...prev, created]);
        toast.success('Subtask created');
      } catch (err: unknown) {
        toast.error(taskActionErrorMessage(err, 'Failed to create subtask'));
      }
    },
    [projectId, setTasks],
  );

  const handleRenameTaskTitle = useCallback(
    async (task: WorkTask, nextTitle: string) => {
      setTitleRenameTaskId(null);
      const trimmed = nextTitle.trim();
      if (!trimmed || trimmed === task.title) return;
      try {
        await optimisticPatchTask(task.id, { title: trimmed });
        toast.success('Task renamed');
      } catch {
        toast.error('Rename failed');
      }
    },
    [optimisticPatchTask],
  );

  function getStatusColor(status: WorkTaskStatus): string {
    switch (status) {
      case 'TODO':
      case 'BACKLOG':
        return 'bg-gray-100 text-gray-800';
      case 'IN_PROGRESS':
      case 'IN_REVIEW':
      case 'BLOCKED':
        return 'bg-blue-100 text-blue-800';
      case 'DONE':
      case 'CANCELED':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  function getStatusLabel(status: WorkTaskStatus): string {
    switch (status) {
      case 'BACKLOG':
        return 'Backlog';
      case 'TODO':
        return 'Todo';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'BLOCKED':
        return 'Blocked';
      case 'IN_REVIEW':
        return 'In Review';
      case 'DONE':
        return 'Done';
      case 'CANCELED':
        return 'Canceled';
      default:
        return status;
    }
  }

  function getUserLabel(userId?: string | null): string {
    if (!userId) return 'Unknown';
    const member = workspaceMembers.find((m: any) => m.userId === userId);
    const user = member?.user || (member ? { id: member.userId, email: 'Unknown' } : null);
    if (!user) return userId;
    const displayName = user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email;
    return displayName || userId;
  }

  // PHASE 7 MODULE 7.4: Bulk action handlers
  function toggleTaskSelection(taskId: string) {
    if (loading) return;
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }

  function toggleSelectAll() {
    if (loading) return;
    if (selectedTaskIds.size === sortedVisibleTasks.length && sortedVisibleTasks.length > 0) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(sortedVisibleTasks.map((t) => t.id)));
    }
  }

  function clearSelection() {
    setSelectedTaskIds(new Set());
    setBulkAction(null);
  }

  async function handleBulkUpdate() {
    if (selectedTaskIds.size === 0 || !bulkAction) return;
    if (loading) return;
    if (hasWorkspaceMismatch) {
      handleWorkspaceError();
      return;
    }

    setBulkProcessing(true);
    const ids = Array.from(selectedTaskIds);

    try {
      const patch: UpdateTaskPatch = {};

      if (bulkAction === 'status') {
        patch.status = bulkStatus;
        if (bulkStatus === 'CANCELED') {
          const needsReason = ids.some((id) => {
            const task = tasks.find((item) => item.id === id);
            if (!task || task.status === 'CANCELED') return false;
            return closureRequiredForPhase(
              useGates,
              closurePhases.find((phase) => phase.id === task.phaseId),
            );
          });
          if (needsReason) {
            setClosurePrompt({ taskIds: ids, kind: 'CANCELLED', bulk: true });
            setBulkProcessing(false);
            return;
          }
        }
      } else if (bulkAction === 'assign') {
        patch.assigneeUserId = bulkAssigneeId || null;
      } else if (bulkAction === 'unassign') {
        patch.assigneeUserId = null;
      } else if (bulkAction === 'dueDate') {
        patch.dueDate = bulkDueDate || null;
      } else if (bulkAction === 'clearDueDate') {
        patch.dueDate = null;
      }

      // Use atomic bulk API (PATCH /work/tasks/actions/bulk-update)
      try {
        const bulkInput: any = { taskIds: ids };
        if (patch.status !== undefined) bulkInput.status = patch.status;
        if (patch.assigneeUserId !== undefined) bulkInput.assigneeUserId = patch.assigneeUserId;
        if (patch.dueDate !== undefined) bulkInput.dueDate = patch.dueDate;

        const result = await bulkUpdate(bulkInput);
        if (result.blockedCount && result.blockedCount > 0) {
          notifyGovernanceBulkPartialSuccess(result);
        } else if (!notifyBulkSkipped(result)) {
          toast.success(`Updated ${result.updated} task${result.updated > 1 ? 's' : ''}`);
        }

        // Refresh task list to reflect changes
        invalidateStatsCache(projectId);
        window.dispatchEvent(new CustomEvent('task:changed', { detail: { projectId } }));
        if (workListKey) {
          await queryClient.invalidateQueries({ queryKey: workListKey });
        }
      } catch (bulkError: any) {
        const { code, message, invalidTransitions } = getErrorDetails(bulkError);

        if (code === ERR_VALIDATION_ERROR && invalidTransitions?.length) {
          const details = invalidTransitions.slice(0, 3)
            .map((t: any) => `${t.from} → ${t.to}`)
            .join(', ');
          toast.error(`Bulk update failed: ${details}`);
        } else if (code === ERR_WORKSPACE_REQUIRED) {
          handleWorkspaceError();
        } else if (notifyGovernanceRuleBlocked(bulkError, { projectId, workspaceId })) {
          // Governance toast already shown
        } else {
          toast.error(message || 'Bulk update failed');
        }
      }

      setSelectedTaskIds(new Set());
      setBulkAction(null);
    } catch (error: any) {
      console.error('Bulk update failed:', error);
      const { code, message } = getErrorDetails(error);
      if (code === ERR_WORKSPACE_REQUIRED) {
        handleWorkspaceError();
      } else {
        toast.error(message || 'Bulk update failed');
      }
    } finally {
      setBulkProcessing(false);
    }
  }

  function requestBulkDelete() {
    if (selectedTaskIds.size === 0) return;
    if (loading) return;
    if (hasWorkspaceMismatch) {
      handleWorkspaceError();
      return;
    }
    setPendingBulkDeleteCount(selectedTaskIds.size);
  }

  async function executeBulkDelete() {
    if (selectedTaskIds.size === 0) return;
    if (loading) return;
    if (hasWorkspaceMismatch) {
      handleWorkspaceError();
      return;
    }

    const ids = Array.from(selectedTaskIds);

    // FIX #2: Store full task list snapshot before optimistic removal (preserves order)
    const snapshotBeforeDelete = [...tasks];
    const idsSet = new Set(ids);

    setTasks(prev => prev.filter(t => !idsSet.has(t.id)));
    setSelectedTaskIds(new Set());
    setBulkAction(null);
    setBulkProcessing(true);

    try {
      const results = await Promise.allSettled(ids.map((id) => deleteTask(id)));

      // FIX #1: Track failed IDs by index, not by slice assumption
      const succeededIds = new Set<string>();
      const failedIds = new Set<string>();
      results.forEach((r, idx) => {
        const taskId = ids[idx];
        if (!taskId) return; // Safety check
        if (r.status === 'fulfilled') {
          succeededIds.add(taskId);
        } else {
          failedIds.add(taskId);
        }
      });

      if (succeededIds.size > 0) {
        toast.success(`Deleted ${succeededIds.size} task${succeededIds.size > 1 ? 's' : ''}`);
        // Invalidate stats cache and dispatch event for KPI invalidation
        invalidateStatsCache(projectId);
        window.dispatchEvent(new CustomEvent('task:changed', { detail: { projectId } }));
        // Refresh deleted panel if open to show newly deleted tasks
        refreshDeletedPanelIfOpen();
        // Reload from server so cascaded child deletes and caps stay consistent
        if (workListKey) {
          await queryClient.invalidateQueries({ queryKey: workListKey });
        }
      }

      if (failedIds.size > 0) {
        if (succeededIds.size === 0) {
          setTasks(snapshotBeforeDelete);
        }

        const firstRejected = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
        const { code, message } = getErrorDetails(firstRejected?.reason);
        if (code === ERR_WORKSPACE_REQUIRED) {
          handleWorkspaceError();
        } else {
          toast.error(`${failedIds.size} task${failedIds.size > 1 ? 's' : ''} failed to delete: ${message}`);
        }
      }
    } catch (error: any) {
      console.error('Bulk delete failed:', error);
      // FULL ROLLBACK: Restore entire snapshot
      setTasks(snapshotBeforeDelete);

      const { code, message } = getErrorDetails(error);
      if (code === ERR_WORKSPACE_REQUIRED) {
        handleWorkspaceError();
      } else {
        toast.error(message || 'Bulk delete failed');
      }
    } finally {
      setBulkProcessing(false);
    }
  }

  // ============================================================
  // ADMIN ONLY: Recently Deleted Tasks
  // ============================================================

  async function loadDeletedTasks() {
    if (!projectId || !isAdmin) return;
    if (hasWorkspaceMismatch) {
      handleWorkspaceError();
      return;
    }
    // In-flight guard to prevent duplicate concurrent fetches
    if (deletedInFlight.current) return;
    deletedInFlight.current = true;

    setDeletedLoading(true);
    try {
      const PAGE = WORK_TASK_LIST_PAGE_SIZE;
      const MAX_PAGES = 25;
      const byId = new Map<string, WorkTask>();
      let offset = 0;
      let hitPageCap = false;
      for (let p = 0; p < MAX_PAGES; p++) {
        const result = await listTasks({
          projectId,
          includeDeleted: true,
          limit: PAGE,
          offset,
        });
        const items = Array.isArray(result.items) ? result.items : [];
        for (const t of items) {
          if (t.deletedAt) {
            byId.set(t.id, t);
          }
        }
        if (items.length < PAGE) {
          break;
        }
        offset += PAGE;
        if (p === MAX_PAGES - 1) {
          hitPageCap = true;
          break;
        }
      }
      const deleted = Array.from(byId.values()).sort((a, b) => {
        const ta = a.deletedAt ? new Date(a.deletedAt).getTime() : 0;
        const tb = b.deletedAt ? new Date(b.deletedAt).getTime() : 0;
        return tb - ta;
      });
      setDeletedTasks(deleted);
      if (hitPageCap) {
        toast.info('Not all deleted tasks could be loaded in one session (page limit reached).');
      }
    } catch (error: any) {
      console.error('Failed to load deleted tasks:', error);
      const { code } = getErrorDetails(error);
      if (code === ERR_WORKSPACE_REQUIRED) {
        handleWorkspaceError();
      }
      // Silent failure for deleted panel - don't toast
    } finally {
      deletedInFlight.current = false;
      setDeletedLoading(false);
    }
  }

  async function handleRestoreTask(taskId: string) {
    if (!isAdmin) return;
    if (hasWorkspaceMismatch) {
      handleWorkspaceError();
      return;
    }

    // Disable row during restore
    setRestoringTaskIds(prev => new Set(prev).add(taskId));

    try {
      const restored = await restoreTask(taskId);

      // Remove from deleted list
      setDeletedTasks(prev => prev.filter(t => t.id !== taskId));

      // Add to active list if it matches current view (same project)
      // Prevent duplicates by checking if task already exists, then merge or insert at top
      if (restored.projectId === projectId) {
        setTasks(prev => {
          const exists = prev.some(t => t.id === restored.id);
          if (exists) {
            // Replace existing (edge case: stale data)
            return prev.map(t => t.id === restored.id ? restored : t);
          }
          // Insert at top (MVP: consistent top insertion for restored tasks)
          return [restored, ...prev];
        });
      }

      // Invalidate stats cache and dispatch events
      invalidateStatsCache(projectId);
      window.dispatchEvent(new CustomEvent('task:changed', { detail: { projectId } }));

      // RISK #2 MITIGATION: If restored task has a phaseId, also trigger plan refresh
      // This handles edge case where task's phase may have changed state
      if (restored.phaseId) {
        window.dispatchEvent(new CustomEvent('plan:changed', { detail: { projectId } }));
      }

      toast.success('Task restored');
    } catch (error: any) {
      console.error('Failed to restore task:', error);
      const { code, message } = getErrorDetails(error);

      if (code === ERR_WORKSPACE_REQUIRED) {
        handleWorkspaceError();
      } else if (code === 'TASK_NOT_FOUND') {
        toast.error('Task not found or already permanently deleted');
        // Remove from list since it's gone
        setDeletedTasks(prev => prev.filter(t => t.id !== taskId));
      } else if (code === 'TASK_NOT_DELETED') {
        // Treat as success from UX standpoint - task is already active
        // Remove from deleted list silently, no error toast, NO full loadTasks
        setDeletedTasks(prev => prev.filter(t => t.id !== taskId));
      } else {
        toast.error(message || 'Failed to restore task');
      }
    } finally {
      // Always clear restoring state
      setRestoringTaskIds(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  }

  // Refresh deleted panel when it's open and count might have changed
  function refreshDeletedPanelIfOpen() {
    if (showDeletedPanel && isAdmin) {
      loadDeletedTasks();
    }
  }

  // Load deleted tasks when panel opens or projectId changes while panel is open
  useEffect(() => {
    if (!showDeletedPanel || !isAdmin) return;

    // Reset and reload when projectId changes to show correct project's deleted tasks
    setDeletedTasks([]);
    loadDeletedTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeletedPanel, isAdmin, projectId]);

  function formatActivity(activity: TaskActivityItem): string {
    const actor = getUserLabel(activity.userId);
    return formatTaskActivitySentence(activity.type, actor, activity.payload);
  }

  function renderActivitiesTableCell(task: WorkTask, col: ProjectColumnKey): ReactNode {
    switch (col) {
      case 'title':
        if (titleRenameTaskId === task.id) {
          return (
            <input
              type="text"
              defaultValue={task.title}
              className="w-full rounded border border-slate-300 px-2 py-0.5 text-sm font-medium dark:border-slate-600 dark:bg-slate-800"
              autoFocus
              data-testid={`activities-rename-input-${task.id}`}
              onFocus={(e) => e.currentTarget.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleRenameTaskTitle(task, e.currentTarget.value);
                if (e.key === 'Escape') setTitleRenameTaskId(null);
              }}
              onBlur={(e) => void handleRenameTaskTitle(task, e.currentTarget.value)}
            />
          );
        }
        return (
          <span className="font-medium text-gray-900 dark:text-slate-100">{task.title}</span>
        );
      case 'assignee':
        if (canEdit) {
          return (
            <select
              value={task.assigneeUserId ?? ''}
              onChange={(e) =>
                void optimisticPatchTask(task.id, {
                  assigneeUserId: e.target.value || null,
                })
              }
              className="max-w-full rounded border border-slate-200 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            >
              <option value="">Unassigned</option>
              {assigneePool.map((member) => {
                const user = member.user || { id: member.userId, email: 'Unknown' };
                const displayName =
                  user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.email;
                return (
                  <option key={member.userId} value={member.userId}>
                    {displayName}
                  </option>
                );
              })}
            </select>
          );
        }
        return (
          <span className="text-slate-700 dark:text-slate-200">
            {task.assigneeUserId ? getUserLabel(task.assigneeUserId) : '—'}
          </span>
        );
      case 'status': {
        if (!canEdit) {
          return (
            <span className={`rounded px-2 py-1 text-xs ${getStatusColor(task.status)}`}>
              {getStatusLabel(task.status)}
            </span>
          );
        }
        const next = getAllowedTransitions(task.status);
        const isPending = pendingStatusIds.has(task.id);
        const statusError = statusActionErrors[task.id];
        // Terminal statuses (e.g. optimistic DONE) still need pending treatment —
        // do not collapse to a confirmed-looking span while the write is in flight.
        if (next.length === 0 && !isPending) {
          return (
            <div className="flex min-w-0 flex-col gap-1">
              <span className={`rounded px-2 py-1 text-xs ${getStatusColor(task.status)}`}>
                {getStatusLabel(task.status)}
              </span>
              {statusError ? (
                <p
                  className="text-[11px] font-medium leading-snug text-red-700"
                  role="alert"
                  data-testid={`task-status-error-${task.id}`}
                >
                  {statusError}
                </p>
              ) : null}
            </div>
          );
        }
        return (
          <div className="flex min-w-0 flex-col gap-1">
            <div
              className={`flex items-center gap-1 ${isPending ? 'opacity-60' : ''}`}
              data-testid={`task-status-pending-${task.id}`}
              data-pending={isPending ? 'true' : undefined}
            >
              {isPending ? (
                <Loader2 className="h-3 w-3 shrink-0 animate-spin text-slate-500" aria-hidden />
              ) : null}
              <select
                value={task.status}
                disabled={isPending}
                onChange={(e) => void handleStatusChange(task.id, e.target.value as WorkTaskStatus)}
                className={`max-w-full rounded border border-slate-200 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-800 ${getStatusColor(task.status)}`}
                aria-busy={isPending}
                data-testid={`task-status-select-${task.id}`}
              >
                <option value={task.status}>{getStatusLabel(task.status)}</option>
                {next.map((s) => (
                  <option key={s} value={s}>
                    {getStatusLabel(s)}
                  </option>
                ))}
              </select>
            </div>
            {statusError ? (
              <p
                className="text-[11px] font-medium leading-snug text-red-700"
                role="alert"
                data-testid={`task-status-error-${task.id}`}
              >
                {statusError}
              </p>
            ) : null}
          </div>
        );
      }
      case 'priority':
        if (canEdit) {
          return (
            <select
              value={task.priority}
              onChange={(e) =>
                void optimisticPatchTask(task.id, {
                  priority: e.target.value as WorkTaskPriority,
                })
              }
              className="rounded border border-slate-200 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            >
              {(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          );
        }
        return <SharedCell column="priority" task={task} />;
      case 'startDate':
        if (canEdit) {
          return (
            <input
              type="date"
              value={task.startDate ? task.startDate.slice(0, 10) : ''}
              onChange={(e) =>
                void optimisticPatchTask(task.id, {
                  startDate: e.target.value ? e.target.value : null,
                })
              }
              className="max-w-full rounded border border-slate-200 px-1 py-0.5 text-xs dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            />
          );
        }
        return task.startDate ? formatCalendarDate(task.startDate) || '—' : '—';
      case 'dueDate':
        if (canEdit) {
          return (
            <input
              type="date"
              value={task.dueDate ? task.dueDate.slice(0, 10) : ''}
              onChange={(e) =>
                void optimisticPatchTask(task.id, {
                  dueDate: e.target.value ? e.target.value : null,
                })
              }
              className="max-w-full rounded border border-slate-200 px-1 py-0.5 text-xs dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            />
          );
        }
        return task.dueDate ? formatCalendarDate(task.dueDate) || '—' : '—';
      case 'duration': {
        const d = computeDurationDays(task.startDate, task.dueDate);
        return d > 0 ? String(d) : '—';
      }
      case 'completion': {
        const subs = (taskChildrenByParent.get(task.id) ?? []).filter((c) => !c.deletedAt);
        const subSt = subs.map((c) => c.status);
        const pct = computeTaskCompletion(
          task.status,
          subSt.length > 0 ? subSt : undefined,
        );
        return (
          <div className="flex items-center gap-2" data-testid={`activities-completion-${task.id}`}>
            <CompletionBar percent={pct} />
          </div>
        );
      }
      case 'description':
        return <SharedCell column="description" task={task} />;
      case 'remarks': {
        const r = task.remarks?.trim();
        if (!r) return '—';
        const short = r.length > 80 ? `${r.slice(0, 80)}…` : r;
        return (
          <span className="text-slate-600 dark:text-slate-300" title={r}>
            {short}
          </span>
        );
      }
      case 'tags':
      case 'taskType':
      case 'estimateHours':
      case 'actualHours':
        return <SharedCell column={col} task={task} />;
      case 'storyPoints':
        if (canEdit) {
          return (
            <input
              key={`${task.id}-sp-${task.estimatePoints ?? 'x'}`}
              type="number"
              min={0}
              defaultValue={task.estimatePoints ?? ''}
              className="w-20 rounded border border-slate-200 px-1 py-0.5 text-xs dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              onBlur={(e) => {
                const v = e.currentTarget.value;
                const n = v === '' ? null : Number(v);
                if (n !== null && Number.isNaN(n)) return;
                if (n === task.estimatePoints) return;
                void optimisticPatchTask(task.id, { estimatePoints: n });
              }}
            />
          );
        }
        return <SharedCell column="storyPoints" task={task} />;
      case 'sprint':
        return (
          <SprintCell
            taskId={task.id}
            iterationId={task.iterationId ?? null}
            sprintMap={sprintMap}
            activeSprints={activeSprints}
            planningSprints={planningSprints}
            canEdit={canEdit}
            onReassign={handleSprintReassign}
          />
        );
      case 'dateCreated':
      case 'dateUpdated':
      case 'dateDone':
      case 'dependencies':
      case 'parentTask':
        return <SharedCell column={col} task={task} loadedTasks={tasks} />;
      case 'approvalStatus':
        return task.approvalStatus;
      case 'documentRequired':
        return task.documentRequired ? 'Yes' : 'No';
      default:
        return '—';
    }
  }

  if (loading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow dark:bg-slate-900">
        <div className="text-center text-gray-500 dark:text-slate-400">Loading tasks...</div>
      </div>
    );
  }

  const detailColSpan =
    activitiesTableColumns.length + attributeTrailingColCount + (canEdit ? 3 : 2);

  return (
    <div
      id="task-list-section"
      className="rounded-lg bg-white p-6 text-slate-900 shadow dark:bg-slate-900 dark:text-slate-100"
    >
      <GovernanceBlockBanner projectId={projectId} workspaceId={workspaceId} className="mb-4" />
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Activities</h2>
        {canEdit && (
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="text-sm"
          >
            + New Task
          </Button>
        )}
      </div>

      {/* Phase 3: Project-linked documents banner — managed in Overview, surfaced here for execution context */}
      {projectDocs.length > 0 && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Project documents
            </span>
            <span className="text-[11px] text-slate-400">· managed in Overview</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {projectDocs.slice(0, 8).map((doc) => (
              <span
                key={doc.id}
                className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700"
              >
                📄 {doc.title}
              </span>
            ))}
            {projectDocs.length > 8 && (
              <span className="inline-flex items-center rounded-full bg-white border border-slate-200 px-2.5 py-1 text-xs text-slate-500">
                +{projectDocs.length - 8} more
              </span>
            )}
          </div>
        </div>
      )}

      {taskListMayBeIncomplete && (
        <div
          className="mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs text-amber-800"
          data-testid="task-list-limit-banner"
        >
          Showing first {WORK_TASK_LIST_PAGE_SIZE} tasks. Some tasks may not be visible.
        </div>
      )}

      {/* Create Task Form */}
      {showCreateForm && canEdit && (
        <form onSubmit={handleCreateTask} className="mb-6 p-4 border rounded-lg bg-gray-50">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                required
                className="w-full px-3 py-2 border rounded-md"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                rows={2}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  name="type"
                  defaultValue="TASK"
                  className="w-full px-3 py-2 border rounded-md"
                  data-testid="task-create-type"
                >
                  {WORK_TASK_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assignee
                </label>
                <select
                  name="assigneeId"
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Unassigned</option>
                  {assigneePool.map((member) => {
                    const user = member.user || { id: member.userId, email: 'Unknown' };
                    const displayName = user.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user.email;
                    return (
                      <option key={member.userId} value={member.userId}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  className="w-full px-3 py-2 border rounded-md"
                  data-testid="task-create-start-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  name="dueDate"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateForm(false)}
                  disabled={creating}
                >
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Creating...' : 'Create Task'}
              </Button>
            </div>
          </div>
        </form>
      )}

      {/* PHASE 7 MODULE 7.4: Bulk Action Bar */}
      {selectedTaskIds.size > 0 && canEdit && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-900">
              {selectedTaskIds.size} task{selectedTaskIds.size > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={clearSelection}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Clear
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {bulkAction === null && (
              <>
                <Button
                  onClick={() => setBulkAction('status')}
                  variant="ghost"
                  className="text-sm"
                  disabled={bulkProcessing || loading}
                >
                  Change Status
                </Button>
                <Button
                  onClick={() => setBulkAction('assign')}
                  variant="ghost"
                  className="text-sm"
                  disabled={bulkProcessing || loading}
                >
                  Assign To
                </Button>
                <Button
                  onClick={() => setBulkAction('dueDate')}
                  variant="ghost"
                  className="text-sm"
                  disabled={bulkProcessing || loading}
                >
                  Set Due Date
                </Button>
                <Button
                  onClick={() => setBulkAction('clearDueDate')}
                  variant="ghost"
                  className="text-sm"
                  disabled={bulkProcessing || loading}
                >
                  Clear Due Date
                </Button>
                <Button
                  onClick={() => setBulkAction('unassign')}
                  variant="ghost"
                  className="text-sm"
                  disabled={bulkProcessing || loading}
                >
                  Unassign
                </Button>
                {isAdmin && (
                  <Button
                    onClick={requestBulkDelete}
                    variant="ghost"
                    className="text-sm text-red-600 border-red-300 hover:bg-red-50"
                    disabled={bulkProcessing || loading}
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
            {bulkAction === 'status' && (
              <div className="flex items-center gap-2">
                <select
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value as WorkTaskStatus)}
                  className="px-2 py-1 border rounded text-sm"
                >
                  <option value="TODO">Todo</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="DONE">Done</option>
                </select>
                <Button onClick={handleBulkUpdate} className="text-sm" disabled={bulkProcessing || loading}>
                  {bulkProcessing ? 'Updating...' : 'Update'}
                </Button>
                <Button onClick={() => setBulkAction(null)} variant="ghost" className="text-sm">
                  Cancel
                </Button>
              </div>
            )}
            {bulkAction === 'assign' && (
              <div className="flex items-center gap-2">
                <select
                  value={bulkAssigneeId}
                  onChange={(e) => setBulkAssigneeId(e.target.value)}
                  className="px-2 py-1 border rounded text-sm"
                >
                  <option value="">Unassigned</option>
                  {assigneePool.map((member) => {
                    const user = member.user || { id: member.userId, email: 'Unknown' };
                    const displayName = user.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user.email;
                    return (
                      <option key={member.userId} value={member.userId}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
                <Button onClick={handleBulkUpdate} className="text-sm" disabled={bulkProcessing || loading}>
                  {bulkProcessing ? 'Updating...' : 'Update'}
                </Button>
                <Button onClick={() => setBulkAction(null)} variant="ghost" className="text-sm">
                  Cancel
                </Button>
              </div>
            )}
            {bulkAction === 'dueDate' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={bulkDueDate}
                  onChange={(e) => setBulkDueDate(e.target.value)}
                  className="px-2 py-1 border rounded text-sm"
                />
                <Button onClick={handleBulkUpdate} className="text-sm" disabled={bulkProcessing || loading}>
                  {bulkProcessing ? 'Updating...' : 'Update'}
                </Button>
                <Button onClick={() => setBulkAction(null)} variant="ghost" className="text-sm">
                  Cancel
                </Button>
              </div>
            )}
            {(bulkAction === 'clearDueDate' || bulkAction === 'unassign') && (
              <div className="flex items-center gap-2">
                <Button onClick={handleBulkUpdate} className="text-sm" disabled={bulkProcessing || loading}>
                  {bulkProcessing ? 'Updating...' : 'Update'}
                </Button>
                <Button onClick={() => setBulkAction(null)} variant="ghost" className="text-sm">
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Task List — Phase 8B: methodology-driven table (shared column registry) */}
      {tasks.length === 0 ? (
        <div className="py-8 text-center text-gray-500 dark:text-slate-400">
          <p>No tasks yet</p>
          {canEdit && (
            <Button
              onClick={() => setShowCreateForm(true)}
              variant="ghost"
              className="mt-4"
            >
              Create first task
            </Button>
          )}
        </div>
      ) : sortedVisibleTasks.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-500 dark:text-slate-400">
          <p>No tasks match your search or filters.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/80">
                {canEdit && (
                  <th scope="col" className="sticky top-0 z-[1] w-10 px-2 py-2 text-left align-middle">
                    <input
                      type="checkbox"
                      aria-label="Select all visible tasks"
                      checked={
                        selectedTaskIds.size === sortedVisibleTasks.length && sortedVisibleTasks.length > 0
                      }
                      onChange={toggleSelectAll}
                      disabled={loading}
                      className="h-4 w-4 rounded border-gray-300 dark:border-slate-600"
                    />
                  </th>
                )}
                {activitiesTableColumns.map((col) => {
                  const def = COLUMN_REGISTRY[col];
                  return (
                    <th
                      key={col}
                      scope="col"
                      className={`sticky top-0 z-[1] whitespace-nowrap border-b border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-400 ${def.width}`}
                    >
                      {def.label}
                    </th>
                  );
                })}
                {visibleAttributeDefinitions.map((attrDef) => (
                  <th
                    key={attrDef.id}
                    scope="col"
                    className="sticky top-0 z-[1] min-w-[120px] cursor-pointer whitespace-nowrap border-b border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-400"
                    onClick={() => handleAttributeHeaderSort(attrDef.id)}
                    data-testid={`activities-attr-header-${attrDef.id}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {attrDef.label}
                      {attributeSort?.defId !== attrDef.id ? (
                        <ArrowUpDown className="h-3 w-3 text-slate-400" />
                      ) : attributeSort.dir === 'asc' ? (
                        <ArrowUp className="h-3 w-3 text-indigo-600" />
                      ) : (
                        <ArrowDown className="h-3 w-3 text-indigo-600" />
                      )}
                    </span>
                  </th>
                ))}
                <th
                  scope="col"
                  className="sticky top-0 z-[1] relative w-8 border-b border-slate-200 bg-slate-50 px-1 py-2 dark:border-slate-700 dark:bg-slate-800/80"
                >
                  <button
                    ref={attributePanelAnchorRef}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAttributePanel((v) => !v);
                    }}
                    className="flex h-6 w-6 items-center justify-center rounded text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700"
                    aria-label="Add fields" title="Add fields"
                    aria-expanded={showAttributePanel}
                    data-testid="activities-attribute-column-add-btn"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </th>
                {canEdit && (
                  <th
                    scope="col"
                    className="sticky top-0 z-[1] w-10 border-b border-slate-200 bg-slate-50 px-2 py-2 dark:border-slate-700 dark:bg-slate-800/80"
                    aria-label="Row actions"
                  />
                )}
                <th
                  scope="col"
                  className="sticky top-0 z-[1] w-[220px] border-b border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-400"
                >
                  Panels
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedVisibleTasks.map((task) => {
                const urlParams = new URLSearchParams(window.location.search);
                const hlId = urlParams.get('taskId');
                const isHighlighted = hlId === task.id;
                const rowExpanded =
                  !!showAC[task.id] ||
                  !!showComments[task.id] ||
                  !!showActivity[task.id] ||
                  !!showDeps[task.id];

                return (
                  <Fragment key={task.id}>
                    <tr
                      data-task-id={task.id}
                      className={`group border-b border-slate-200 dark:border-slate-700 ${
                        isHighlighted ? 'bg-blue-50 ring-2 ring-blue-500 dark:bg-slate-800' : ''
                      } ${
                        selectedTaskIds.has(task.id)
                          ? 'bg-blue-50/90 dark:bg-blue-950/40'
                          : !isHighlighted
                            ? 'bg-white dark:bg-slate-900'
                            : ''
                      } hover:bg-slate-50 dark:hover:bg-slate-800/50`}
                    >
                      {canEdit && (
                        <td className="px-2 py-2 align-middle">
                          <input
                            type="checkbox"
                            checked={selectedTaskIds.has(task.id)}
                            onChange={() => toggleTaskSelection(task.id)}
                            disabled={loading}
                            className="h-4 w-4 rounded border-gray-300 dark:border-slate-600"
                          />
                        </td>
                      )}
                      {activitiesTableColumns.map((col) => (
                        <td
                          key={col}
                          className={`px-3 py-2 align-middle text-slate-800 dark:text-slate-200 ${COLUMN_REGISTRY[col].width}`}
                        >
                          {renderActivitiesTableCell(task, col)}
                        </td>
                      ))}
                      {visibleAttributeDefinitions.map((attrDef) => {
                        const isEditing =
                          editingAttributeCell?.taskId === task.id &&
                          editingAttributeCell?.definitionId === attrDef.id;
                        const cellError =
                          attributeCommitError?.taskId === task.id &&
                          attributeCommitError?.definitionId === attrDef.id
                            ? attributeCommitError.message
                            : null;
                        return (
                          <td
                            key={attrDef.id}
                            className="min-w-[120px] px-3 py-2 align-middle"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <AttributeCell
                              definition={attrDef}
                              value={attributeValues[task.id]?.[attrDef.id]}
                              canEdit={canEdit}
                              isEditing={isEditing}
                              error={cellError}
                              onStartEdit={() =>
                                setEditingAttributeCell({
                                  taskId: task.id,
                                  definitionId: attrDef.id,
                                })
                              }
                              onCommit={(v) => void onCommitAttribute(task.id, attrDef.id, v)}
                              onCancel={() => setEditingAttributeCell(null)}
                            />
                          </td>
                        );
                      })}
                      <td className="w-8 px-1 align-middle" aria-hidden />
                      {canEdit && (
                        <td className="px-2 py-2 align-middle">
                          <ActivitiesTaskRowMenu
                            taskId={task.id}
                            taskTitle={task.title}
                            isSubtask={!!task.parentTaskId}
                            currentPhaseId={task.phaseId}
                            phases={moveMenuPhases(closurePhases, task, useGates).phases}
                            deferralBlocked={moveMenuPhases(closurePhases, task, useGates).deferralBlocked}
                            onMarkNotApplicable={
                              closureRequiredForPhase(
                                useGates,
                                closurePhases.find((phase) => phase.id === task.phaseId),
                              )
                                ? () => setClosurePrompt({ taskIds: [task.id], kind: 'NOT_APPLICABLE' })
                                : undefined
                            }
                            parentTaskCandidates={parentCandidatesFor(task)}
                            onOpen={() => openDetailPanel(task.id)}
                            onRename={() => setTitleRenameTaskId(task.id)}
                            onAddSubtask={() => void handleAddSubtask(task)}
                            onPromoteToTask={
                              task.parentTaskId
                                ? () => void handlePromoteTask(task)
                                : undefined
                            }
                            onMoveToPhase={(phaseId) => void handleMoveTaskToPhase(task.id, phaseId)}
                            onConvertTo={(type) => void handleConvertTask(task, type)}
                            onConvertToSubtask={(parentId) =>
                              void handleConvertToSubtask(task, parentId)
                            }
                            onLinkTo={(relation, targetId) =>
                              void handleLinkTask(task.id, targetId, relation)
                            }
                            onDuplicate={() => void handleDuplicateTask(task)}
                            onCopyLink={() => void handleCopyTaskLink(task.id)}
                            onRequestDelete={() => setPendingDeleteTask(task)}
                          />
                        </td>
                      )}
                      <td className="px-2 py-2 align-middle">
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            onClick={() => toggleAC(task.id)}
                            className="text-xs text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                          >
                            {showAC[task.id] ? 'Hide' : 'Show'} AC ({task.acceptanceCriteria?.length || 0})
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleComments(task.id)}
                            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            {showComments[task.id] ? 'Hide' : 'Show'} Comments ({comments[task.id]?.length || 0})
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleActivity(task.id)}
                            className="text-xs text-gray-600 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200"
                          >
                            {showActivity[task.id] ? 'Hide' : 'Show'} Activity
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleDeps(task.id)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            {showDeps[task.id] ? 'Hide' : 'Show'} Deps
                          </button>
                        </div>
                      </td>
                    </tr>
                    {rowExpanded && (
                      <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-950/40">
                        <td colSpan={detailColSpan} className="px-4 py-3">
                          {showAC[task.id] && (
                            <AcceptanceCriteriaEditor
                              items={task.acceptanceCriteria || []}
                              onSave={(items) => handleSaveAC(task.id, items)}
                              readOnly={!canEdit}
                            />
                          )}

                          {showComments[task.id] && (
                            <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-600">
                              <h4 className="mb-2 text-sm font-medium dark:text-slate-100">Comments</h4>
                              <div className="mb-3 space-y-2">
                                {comments[task.id]?.map((comment) => (
                                  <div
                                    key={comment.id}
                                    className="rounded bg-gray-50 p-2 text-sm dark:bg-slate-900"
                                  >
                                    <div className="font-medium text-gray-700 dark:text-slate-200">
                                      {getUserLabel(comment.authorUserId)}
                                    </div>
                                    <div className="mt-1 text-gray-600 dark:text-slate-300">{comment.body}</div>
                                    <div className="mt-1 text-xs text-gray-400 dark:text-slate-500">
                                      {new Date(comment.createdAt).toLocaleString()}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {canEdit && !projectClosed && (
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={newComment[task.id] || ''}
                                    onChange={(e) =>
                                      setNewComment((prev) => ({ ...prev, [task.id]: e.target.value }))
                                    }
                                    placeholder="Add a comment..."
                                    className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleAddComment(task.id);
                                      }
                                    }}
                                  />
                                  <Button
                                    onClick={() => handleAddComment(task.id)}
                                    disabled={!newComment[task.id]?.trim() || postingComment[task.id]}
                                    className="text-sm"
                                  >
                                    {postingComment[task.id] ? 'Posting...' : 'Post'}
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}

                          {showActivity[task.id] && (
                            <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-600">
                              <h4 className="mb-2 text-sm font-medium dark:text-slate-100">Activity</h4>
                              <div className="space-y-2">
                                {activities[task.id]?.map((activity) => (
                                  <div key={activity.id} className="text-xs text-gray-600 dark:text-slate-300">
                                    <span className="font-medium">{formatActivity(activity)}</span>
                                    <span className="ml-2 text-gray-400 dark:text-slate-500">
                                      {new Date(activity.createdAt).toLocaleString()}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {showDeps[task.id] && (
                            <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-600">
                              <h4 className="mb-2 text-sm font-medium dark:text-slate-100">Dependencies</h4>
                              <div className="mb-3">
                                <p className="mb-1 text-xs font-medium text-gray-500 dark:text-slate-400">
                                  Blocked by
                                </p>
                                {deps[task.id]?.predecessors?.length ? (
                                  <div className="space-y-1">
                                    {deps[task.id].predecessors.map((dep) => (
                                      <div
                                        key={dep.id}
                                        className="flex items-center justify-between rounded bg-red-50 p-2 text-sm dark:bg-red-950/40"
                                      >
                                        <span className="text-gray-700 dark:text-slate-200">
                                          {dep.predecessorTitle || dep.predecessorTaskId}
                                        </span>
                                        {canEdit && (
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveDep(task.id, dep.predecessorTaskId)}
                                            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400"
                                          >
                                            Remove
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-400 dark:text-slate-500">None</p>
                                )}
                              </div>
                              <div className="mb-3">
                                <p className="mb-1 text-xs font-medium text-gray-500 dark:text-slate-400">
                                  Blocking
                                </p>
                                {deps[task.id]?.successors?.length ? (
                                  <div className="space-y-1">
                                    {deps[task.id].successors.map((dep) => (
                                      <div
                                        key={dep.id}
                                        className="flex items-center rounded bg-amber-50 p-2 text-sm dark:bg-amber-950/30"
                                      >
                                        <span className="text-gray-700 dark:text-slate-200">
                                          {dep.successorTitle || dep.successorTaskId}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-400 dark:text-slate-500">None</p>
                                )}
                              </div>
                              {canEdit && (
                                <div>
                                  <p className="mb-1 text-xs font-medium text-gray-500 dark:text-slate-400">
                                    Add &quot;blocked by&quot; dependency
                                  </p>
                                  <div className="flex gap-2">
                                    <select
                                      value={depSearch[task.id] || ''}
                                      onChange={(e) =>
                                        setDepSearch((prev) => ({ ...prev, [task.id]: e.target.value }))
                                      }
                                      className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                                    >
                                      <option value="">Select a task...</option>
                                      {tasks
                                        .filter((t) => t.id !== task.id && !t.deletedAt)
                                        .map((t) => (
                                          <option key={t.id} value={t.id}>
                                            {t.title}
                                          </option>
                                        ))}
                                    </select>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        depSearch[task.id] && handleAddDep(task.id, depSearch[task.id])
                                      }
                                      disabled={!depSearch[task.id] || addingDep[task.id]}
                                      className="rounded-md bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                                    >
                                      {addingDep[task.id] ? 'Adding...' : 'Add'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ADMIN ONLY: Recently Deleted Tasks Panel */}
      {isAdmin && (
        <div className="mt-6 border-t pt-4">
          <button
            type="button"
            onClick={() => setShowDeletedPanel(prev => !prev)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <span className={`transform transition-transform ${showDeletedPanel ? 'rotate-90' : ''}`}>
              ▶
            </span>
            <span>Recently deleted</span>
            {deletedTasks.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-full">
                {deletedTasks.length}
              </span>
            )}
          </button>

          {showDeletedPanel && (
            <div className="mt-3 space-y-2">
              {deletedLoading ? (
                <div className="text-sm text-gray-500 py-2">Loading deleted tasks...</div>
              ) : deletedTasks.length === 0 ? (
                <div className="text-sm text-gray-500 py-2">No deleted tasks</div>
              ) : (
                deletedTasks.map(task => (
                  <div
                    key={task.id}
                    className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 ${
                      restoringTaskIds.has(task.id) ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-700 line-through">
                        {task.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Deleted {task.deletedAt ? new Date(task.deletedAt).toLocaleDateString() : 'recently'}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleRestoreTask(task.id)}
                      disabled={restoringTaskIds.has(task.id)}
                      className="ml-3 text-sm"
                      variant="ghost"
                    >
                      {restoringTaskIds.has(task.id) ? 'Restoring...' : 'Restore'}
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {pendingBulkDeleteCount !== null && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="activities-bulk-delete-dialog-title"
          data-testid="activities-bulk-delete-dialog"
        >
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl dark:bg-slate-900">
            <h3
              id="activities-bulk-delete-dialog-title"
              className="text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              Delete {pendingBulkDeleteCount} task{pendingBulkDeleteCount > 1 ? 's' : ''}?
            </h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingBulkDeleteCount(null)}
                className="rounded-md px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setPendingBulkDeleteCount(null);
                  void executeBulkDelete();
                }}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteTask && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="activities-delete-task-dialog-title"
          data-testid="activities-delete-task-dialog"
        >
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl dark:bg-slate-900">
            <h3
              id="activities-delete-task-dialog-title"
              className="text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              Delete this task?
            </h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingDeleteTask(null)}
                className="rounded-md px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const task = pendingDeleteTask;
                  setPendingDeleteTask(null);
                  if (task) void executeDeleteTask(task);
                }}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {detailPanelTaskId && (() => {
        const detailTask = tasks.find((t) => t.id === detailPanelTaskId);
        if (!detailTask) {
          queueMicrotask(closeDetailPanel);
          return null;
        }
        const detailPhaseName =
          planPhases.find((p) => p.id === detailTask.phaseId)?.name ?? '';
        const detailSubtasks = tasks.filter(
          (t) => t.parentTaskId === detailTask.id && !t.deletedAt,
        );
        return (
          <TaskDetailPanel
            task={detailTask}
            phaseName={detailPhaseName}
            phases={closurePhases}
            useGates={useGates}
            members={assigneePool as WorkspaceMember[]}
            statusGroups={ACTIVITIES_STATUS_GROUPS}
            subtasks={detailSubtasks}
            onPatch={patchTaskForDetailPanel}
            onOpenTask={openDetailPanel}
            onAddSubtask={async (title) => {
              try {
                const created = await createTask({
                  projectId,
                  title,
                  parentTaskId: detailTask.id,
                  phaseId: detailTask.phaseId ?? undefined,
                });
                setTasks((prev) => [...prev, created]);
                toast.success('Subtask created');
                return created;
              } catch (err: unknown) {
                toast.error(taskActionErrorMessage(err, 'Failed to create subtask'));
                return null;
              }
            }}
            onClose={closeDetailPanel}
          />
        );
      })()}

      {closurePrompt ? (
        <TaskClosureReasonDialog
          kind={closurePrompt.kind}
          taskTitle={
            closurePrompt.taskIds.length === 1
              ? tasks.find((task) => task.id === closurePrompt.taskIds[0])?.title ?? 'Task'
              : `${closurePrompt.taskIds.length} tasks`
          }
          onCancel={() => setClosurePrompt(null)}
          onConfirm={async (reason, options) => {
            const prompt = closurePrompt;
            if (prompt.kind === 'DEFERRED' && prompt.phaseId) {
              const body = {
                ...deferralClosureBody(prompt.phaseId, reason),
                ...(options?.acknowledgeStatusReset === true
                  ? { acknowledgeStatusReset: true as const }
                  : {}),
              };
              try {
                await optimisticPatchTask(prompt.taskIds[0], body, { propagateResetAck: true });
                setClosurePrompt(null);
              } catch (err) {
                if (isExecutionLockResetAckRequired(err)) throw err;
                setClosurePrompt(null);
              }
              return;
            }
            setClosurePrompt(null);
            if (prompt.kind !== 'CANCELLED' && prompt.kind !== 'NOT_APPLICABLE') return;
            const body = cancelClosureBody(prompt.kind, reason);
            if (prompt.bulk) {
              notifyBulkSkipped(await bulkUpdate({ taskIds: prompt.taskIds, ...body }));
              if (workListKey) await queryClient.invalidateQueries({ queryKey: workListKey });
              return;
            }
            const updated = await updateTask(prompt.taskIds[0], body);
            setTasks((prev) => prev.map((task) => (task.id === prompt.taskIds[0] ? { ...task, ...updated } : task)));
          }}
        />
      ) : null}

      {showAttributePanel && workspaceId ? (
        <UnifiedWorkFieldsPanel
          anchorRef={attributePanelAnchorRef}
          onClose={() => setShowAttributePanel(false)}
          properties={{
            mode: 'registry',
            dataColumnOrder: pickableColumns,
            hiddenColumns: hiddenPropertyColumns,
            onToggleColumn: togglePropertyColumn,
            governanceActive: true,
            showOptionalPool: false,
          }}
          customFields={{
            available: availableAttributes,
            visibleIds: visibleAttributeIds,
            onToggleColumn: (id, visible) => void toggleAttributeColumn(id, visible),
            onCreated: (def) => void onAttributeCreated(def),
            workspaceId,
            loadFailed: attributesLoadFailed,
            onRetry: retryAttributes,
          }}
        />
      ) : null}

    </div>
  );
}
